import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaChartLine, FaRupeeSign, FaWallet, FaEye, FaCheckCircle, FaClock, FaMoneyBillWave } from 'react-icons/fa';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';

// ============ FIXED: Handle both with and without /api prefix ============
const getBaseURL = () => {
  const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const cleanUrl = url.replace(/\/$/, '');
  if (cleanUrl.endsWith('/api')) {
    return cleanUrl;
  }
  return `${cleanUrl}/api`;
};

const API_URL = getBaseURL();

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalInvestors: 0,
    totalInvestments: 0,
    totalInvestmentAmount: 0,
    pendingInvestments: 0,
    pendingKYC: 0,
    totalWalletBalance: 0,
    recentInvestments: []
  });
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      // ✅ FIXED: Using API_URL with auto /api prefix
      const response = await axios.get(`${API_URL}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);

      // Fetch total wallet balance from all investors
      const walletResponse = await axios.get(`${API_URL}/admin/wallet-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(prev => ({ ...prev, totalWalletBalance: walletResponse.data.totalWalletBalance || 0 }));

      setChartData(response.data.chartData || [
        { month: 'Jan', amount: 50000 }, { month: 'Feb', amount: 65000 }, { month: 'Mar', amount: 80000 },
        { month: 'Apr', amount: 75000 }, { month: 'May', amount: 95000 }, { month: 'Jun', amount: 110000 }
      ]);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTriggerPayout = async () => {
    if (window.confirm("Are you sure you want to trigger the monthly payout for all investors? This will distribute profits to wallets.")) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(`${API_URL}/admin/trigger-payout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          alert('Monthly payout triggered successfully! Profits have been credited.');
          fetchDashboardData();
        }
      } catch (error) {
        console.error('Error triggering payout:', error);
        alert('Failed to trigger payout. Check console for details.');
      }
    }
  };

  const statCards = [
    { title: 'Total Investors', value: stats.totalInvestors, icon: <FaUsers className="text-2xl" />, color: 'bg-blue-500' },
    { title: 'Total Investments', value: stats.totalInvestments, icon: <FaChartLine className="text-2xl" />, color: 'bg-green-500' },
    { title: 'Investment Amount', value: `₹${(stats.totalInvestmentAmount / 10000000).toFixed(2)}Cr`, icon: <FaRupeeSign className="text-2xl" />, color: 'bg-purple-500' },
    { title: 'Pending Verifications', value: stats.pendingInvestments, icon: <FaClock className="text-2xl" />, color: 'bg-orange-500' },
    { title: 'Total Wallet Balance', value: `₹${(stats.totalWalletBalance / 10000000).toFixed(2)}Cr`, icon: <FaMoneyBillWave className="text-2xl" />, color: 'bg-teal-500' },
  ];

  const COLORS = ['#22c55e', '#eab308', '#3b82f6', '#ef4444'];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Assets Dairy Farm</title>
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container mx-auto px-4">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Admin Dashboard</h1>
              <p className="text-gray-600 mt-1">Manage and monitor all platform activities</p>
            </div>
            <button 
              onClick={handleTriggerPayout}
              className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded shadow transition"
            >
              Trigger Monthly Payout
            </button>
          </div>

          {/* Stats Cards - 5 Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
            {statCards.map((stat, idx) => (
              <div key={idx} className={`${stat.color} rounded-xl shadow-lg p-6 text-white transform hover:scale-105 transition`}>
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm opacity-90">{stat.title}</p>
                    <p className="text-2xl font-bold mt-1">{stat.value}</p>
                  </div>
                  <div className="bg-white/20 rounded-lg p-2">{stat.icon}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Investment Growth</h2>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="amount" stroke="#22c55e" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl shadow-md p-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-4">Investment Distribution</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={[
                    { name: 'Basic', value: 30 }, { name: 'Standard', value: 40 },
                    { name: 'Premium', value: 20 }, { name: 'Enterprise', value: 10 }
                  ]} cx="50%" cy="50%" innerRadius={60} outerRadius={100} fill="#8884d8" dataKey="value" label>
                    {[...Array(4)].map((_, idx) => <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Recent Investments */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">Recent Investment Requests</h2>
              <Link to="/admin/investments" className="text-green-600 hover:text-green-700 text-sm">View All →</Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {stats.recentInvestments?.map((inv, idx) => (
                    <tr key={idx}>
                      <td className="px-6 py-4 font-medium">{inv.user?.name}</td>
                      <td className="px-6 py-4 capitalize">{inv.plan}</td>
                      <td className="px-6 py-4">₹{inv.amount.toLocaleString()}</td>
                      <td className="px-6 py-4">{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs rounded-full ${inv.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <Link to={`/admin/investments?view=${inv._id}`} className="text-green-600 hover:text-green-700">
                          <FaEye />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminDashboard;